/**
 * Label + help on the left, Toggle on the right — the standard row layout
 * for boolean config fields (§6.4 / §6.6).
 */

import { useId, type ReactNode } from "react";

import { Toggle } from "../Toggle";
import { cx } from "../../lib/format";

export interface ToggleRowProps {
  label: string;
  help?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Inline error under the row (e.g. outbound-needs-number). */
  error?: string | null;
  /** Extra content shown under the row when checked (e.g. consent warning). */
  children?: ReactNode;
  className?: string;
}

export function ToggleRow({
  label,
  help,
  checked,
  onChange,
  disabled = false,
  error,
  children,
  className,
}: ToggleRowProps) {
  const id = useId();
  return (
    <div className={cx("flex flex-col gap-2", className)}>
      <div className="flex items-start justify-between gap-4">
        <label htmlFor={id} className="flex cursor-pointer flex-col gap-0.5">
          <span className="text-sm text-bright">{label}</span>
          {help && <span className="text-xs text-muted">{help}</span>}
        </label>
        <Toggle id={id} checked={checked} onChange={onChange} disabled={disabled} label={label} />
      </div>
      {error && (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
      {checked && children}
    </div>
  );
}
